import { useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash.debounce';
import { actions } from '../../redux';
import { actions as usersActions } from 'features/users/redux';

const useCreateCashback = onSuccess => {
  const dispatch = useDispatch();
  const locale = useSelector(s => s.locale.locale);

  const providerOptions = [
    { value: 'casino', name: 'casino' },
    { value: 'sport', name: 'sport' },
  ];

  const [state, setState] = useState({
    providerType: providerOptions[0],
    durationDays: '',
    cashbackPercent: '',
    isForAllPlayers: true,
    selectedPlayer: null,
    filterValue: '',
    searchResult: [],
    isLoadingSearchResult: false,
  });

  const handleProviderTypeChange = useCallback(value => {
    const selectedProvider = providerOptions.find(p => p.value === value);
    setState(prev => ({ ...prev, providerType: selectedProvider }));
  }, []);

  const handleDurationDaysChange = useCallback(value => {
    setState(prev => ({ ...prev, durationDays: value }));
  }, []);

  const handleCashbackPercentChange = useCallback(value => {
    setState(prev => ({ ...prev, cashbackPercent: value }));
  }, []);

  const handleIsForAllPlayersChange = useCallback(value => {
    setState(prev => ({
      ...prev,
      isForAllPlayers: value,
      selectedPlayer: value ? null : prev.selectedPlayer,
      filterValue: value ? '' : prev.filterValue,
    }));
  }, []);

  const [playersData, setPlayersData] = useState([]);

  const debouncedSearchUser = useRef(
    debounce(value => {
      if (value.length >= 4) {
        setState(prev => ({ ...prev, isLoadingSearchResult: true }));
        dispatch(
          usersActions.getUsersListForSearch({
            username: value,
            isDirectStructure: true,
            callback: data => {
              if (data) {
                const players = data.filter(user => user.role === '0' || user.role === 0);
                setPlayersData(players);
                setState(prev => ({
                  ...prev,
                  searchResult: players.map(i => i.username),
                  isLoadingSearchResult: false,
                }));
              } else {
                setPlayersData([]);
                setState(prev => ({ ...prev, isLoadingSearchResult: false, searchResult: [] }));
              }
            },
          }),
        );
      }
    }, 800),
  ).current;

  const handleSearchChange = useCallback(
    e => {
      const value = e.target.value;
      setState(prev => ({ ...prev, filterValue: value }));

      if (value.length >= 4) {
        setState(prev => ({ ...prev, isLoadingSearchResult: true }));
        debouncedSearchUser(value);
      } else {
        setState(prev => ({ ...prev, isLoadingSearchResult: false, searchResult: [] }));
        setPlayersData([]);
      }
    },
    [debouncedSearchUser],
  );

  const handleResultClick = useCallback(
    username => {
      const player = playersData.find(p => p.username === username);
      setState(prev => ({
        ...prev,
        selectedPlayer: player,
        filterValue: username,
        searchResult: [],
        isLoadingSearchResult: false,
      }));
    },
    [playersData],
  );

  const handleReset = useCallback(() => {
    setState({
      providerType: providerOptions[0],
      durationDays: '',
      cashbackPercent: '',
      isForAllPlayers: true,
      selectedPlayer: null,
      filterValue: '',
      searchResult: [],
      isLoadingSearchResult: false,
    });
    setPlayersData([]);
  }, []);

  const handleSubmit = useCallback(
    e => {
      e.preventDefault();

      const payload = {
        provider_type: state.providerType.value,
        duration_days: Number(state.durationDays),
        cashback_percent: Number(state.cashbackPercent),
        is_for_all_players: state.isForAllPlayers,
      };

      if (!state.isForAllPlayers && state.selectedPlayer) {
        payload.target_player_username = state.selectedPlayer.username;
      }

      dispatch(
        actions.createCashback(payload, () => {
          handleReset();
          onSuccess && onSuccess();
        }),
      );
    },
    [state, dispatch, handleReset, onSuccess],
  );

  const disabled = !state.durationDays || !state.cashbackPercent || (!state.isForAllPlayers && !state.selectedPlayer);

  return {
    values: {
      state,
      disabled,
      providerOptions,
    },
    handlers: {
      handleProviderTypeChange,
      handleDurationDaysChange,
      handleCashbackPercentChange,
      handleIsForAllPlayersChange,
      handleSearchChange,
      handleResultClick,
      handleSubmit,
      handleReset,
    },
  };
};

export default useCreateCashback;
